import { Button, type ButtonProps } from '@nespresso-design-system/components';
import { hasSubscriptions, type Product } from '@/domain/Product';
import type { PersistedState } from '../../Store';
import type { Translations } from '@/widgets/ProductDetailsHero/config';
import useTranslations from '@/widgets/hooks/useTranslations';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape';
import styles from './PurchaseSection.module.scss';
import { namespace } from '../../Providers.tsx';
import type { Options } from '../../config/index.ts';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import AddToCartButtonDisplayer from '@/widgets/components/controls/AddToCartButtonDisplayer/AddToCartButtonDisplayer.tsx';
import StickyPurchaseSection from '../StickyPurchaseSection/StickyPurchaseSection.tsx';
import { useIntersection } from '@/widgets/hooks/useIntersection.tsx';
import useABTestVariant from '@/widgets/hooks/useABTestVariant.tsx';
import EXPERIMENTS from '@/constants/abExperiments.ts';
import useFixedHeaderHeight from '@/widgets/hooks/useFixedHeaderHeight.tsx';
import { useElementRect } from '@/widgets/hooks/useElementRect.tsx';

export type PurchaseSectionProps = {
  product: Product;
};

export default function PurchaseSection({ product }: PurchaseSectionProps) {
  const { isVisible, elementRef } = useIntersection();
  const elementRect = useElementRect(elementRef);
  const enableStickyAddToCartVariant = useABTestVariant([
    EXPERIMENTS.STICKY_MACHINE_ADD_TO_CARD.V1_ENABLED,
    EXPERIMENTS.STICKY_MACHINE_ADD_TO_CARD.V2_ENABLED
  ]);
  const fixedHeaderHeight = useFixedHeaderHeight();
  const [, setPersistedState] = useQueryStringStore<PersistedState>();
  const { t } = useTranslations<Translations>();
  const { displayNewAddToBagButton } = useWidgetOptions<Options>();
  const additionalText = t(
    'productDetailsHero.pricing.optionalAdditionalText',
    { defaultValue: '' }
  );

  // Note: the sticky section is displayed when the no-sticky section has
  // scrolled past the fixed header height
  const displayStickyPurchaseSection =
    enableStickyAddToCartVariant &&
    !isVisible &&
    fixedHeaderHeight > Number(elementRect?.bottom);

  const openSubscriptionDrawer = () => {
    // probably some tracking here...
    setPersistedState({ openModal: 'subscription-options' });
  };

  const subscriptionButtonLabel = product.inStock
    ? t('productDetailsHero.buyWithSubscriptionButton')
    : t('components.addToCartButton.outOfStockLabel');

  let buttonElement;
  if (product.isSubscriptionOnly) {
    buttonElement = (
      <Button
        label={subscriptionButtonLabel}
        disabled={!product.inStock || !hasSubscriptions(product)}
        onClick={openSubscriptionDrawer}
        className={styles.buyWithSubscription}
        size={'medium' as ButtonProps['size']}
        type="button"
      />
    );
  } else {
    buttonElement = (
      <AddToCartButtonDisplayer
        displayNewAddToBagButton={displayNewAddToBagButton}
        product={product}
        class={styles.button}
        namespace={namespace}
      />
    );
  }

  return (
    <section className={styles.container} ref={elementRef}>
      <StickyPurchaseSection
        isReadyToBeVisible={displayStickyPurchaseSection}
        product={product}
        top={fixedHeaderHeight}
      >
        {buttonElement}
      </StickyPurchaseSection>
      {buttonElement}
      {additionalText && (
        <p className={styles.additionalText}>{additionalText}</p>
      )}
    </section>
  );
}

PurchaseSection.Skeleton = function Skeleton() {
  return (
    <section>
      <SkeletonShape />
    </section>
  );
};
